import type { ReactNode } from 'react';
import { useIsDark } from '../../useIsDark';

/**
 * TwoArchitecturesDiagram — the same capability in two different shapes (blog28, Figure 3).
 *
 * Chart-type-first: an ARCHITECTURE, not a distribution → a purpose-built structure
 * diagram, not a chart. Nothing is plotted and no value is claimed, so there is no
 * chart engine here on purpose (the chart-type table says a figure that fits no chart
 * type is probably not a chart, and this one is not).
 *
 * Left panel: an agent inside one person's account, with that person's apps in the
 * same boundary. Right panel: one company workspace holding the functions, a gate on
 * the outbound edge, and one measure fed by the runs.
 *
 * Accepts `height` because ChartZoom clones its child with a taller height for the
 * lightbox; the panels are laid out with flex so a taller box adds space, not stretch.
 * Accepts `dark` for the theme override (Rule 29).
 */

export const TwoArchitecturesDiagram = ({ height = 460, dark }: { height?: number; dark?: boolean }) => {
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const T = isDark
    ? {
        bg: '#0F172A',
        card: '#0B1220',
        border: '#334155',
        text: '#E2E8F0',
        muted: '#94A3B8',
        personal: '#64748B',
        gate: '#3B82F6',
      }
    : {
        bg: '#FFFFFF',
        card: '#F8FAFC',
        border: '#CBD5E1',
        text: '#334155',
        muted: '#64748B',
        personal: '#64748B',
        gate: '#3B82F6',
      };

  const teal = '#0AB3BF';

  const Node = ({
    label,
    sub,
    color,
    strong,
  }: {
    label: string;
    sub?: string;
    color: string;
    strong?: boolean;
  }) => (
    <div
      className="rounded-lg px-3 py-2 text-center"
      style={{
        border: `1px solid ${strong ? color : T.border}`,
        background: T.card,
        minWidth: '8.5rem',
      }}
    >
      <div className="text-[13px] font-semibold" style={{ color: strong ? color : T.text }}>
        {label}
      </div>
      {sub ? (
        <div className="text-[11px] mt-0.5" style={{ color: T.muted }}>
          {sub}
        </div>
      ) : null}
    </div>
  );

  const Panel = ({ title, tint, children }: { title: string; tint: string; children: ReactNode }) => (
    <div
      className="flex-1 rounded-xl p-3 sm:p-4"
      style={{ border: `1px dashed ${tint}`, background: T.card }}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide mb-3" style={{ color: tint }}>
        {title}
      </div>
      <div className="flex flex-col items-center gap-2">{children}</div>
    </div>
  );

  return (
    <figure className="not-prose">
      <div
        className="rounded-xl p-3"
        style={{ background: T.bg, border: `1px solid ${T.border}`, minHeight: height }}
      >
        <div className="flex flex-col sm:flex-row gap-3" style={{ minHeight: Math.max(height - 24, 240) }}>
          <Panel title="One person's account" tint={T.personal}>
            <Node label="Your ChatGPT account" sub="your logins, your apps" color={T.personal} />
            <div className="text-[11px]" style={{ color: T.muted }}>
              remembers for you, works between your conversations
            </div>
            <Node label="One personal agent" sub="its own cloud computer" color={T.personal} strong />
            <div className="text-[11px] text-center" style={{ color: T.muted }}>
              memory, permission and reach end at the person
            </div>
          </Panel>

          <Panel title="The company's workspace" tint={teal}>
            <Node label="One company" sub="its own rules, its own memory" color={teal} strong />
            <div className="flex flex-wrap justify-center gap-2">
              <Node label="Function" sub="a raise" color={teal} />
              <Node label="Function" sub="sales" color={teal} />
              <Node label="Function" sub="investor relations" color={teal} />
            </div>
            <div
              className="rounded-lg px-3 py-1.5 text-[12px] font-semibold"
              style={{ border: `1px solid ${T.gate}`, color: T.gate, background: T.bg }}
            >
              Gate: you approve before anything leaves
            </div>
            <div className="text-[11px] text-center" style={{ color: T.muted }}>
              every run writes a row &rarr; one measure per company
            </div>
          </Panel>
        </div>
      </div>
      <figcaption className={`mt-3 text-xs leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
        The same capability in two shapes. On the left, memory, permission and reach end where one person's account
        ends. On the right they belong to the company, so a colleague can pick the work up and the number survives
        the person. A structure diagram, not a measurement.
      </figcaption>
    </figure>
  );
};
